import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { placeCharm } from '@clasplab/look-stage';

/**
 * "Complete the look" (sections/cl-complete-look.liquid): draws the suggested charm on the band
 * (builder placement) and adds band + charm in one go. Pre-order lines get their property via
 * window.ClaspLabPreorder. Event: set_added.
 */

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

class ClaspLabCompleteLook extends HTMLElement {
  #busy = false;

  connectedCallback() {
    const img = this.querySelector('[ref="charm"]');
    try {
      const place = JSON.parse(this.dataset.place || 'null');
      if (place && img instanceof HTMLImageElement) placeCharm(img, place, this.dataset.kind);
    } catch (e) {}
    this.querySelector('[ref="add"]')?.addEventListener('click', () => this.#add());
  }

  async #add() {
    if (this.#busy) return;
    this.#busy = true;
    const button = this.querySelector('[ref="add"]');
    const error = this.querySelector('[ref="error"]');
    button?.setAttribute('aria-busy', 'true');
    if (error instanceof HTMLElement) error.hidden = true;

    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const line = (/** @type {any} */ item, /** @type {boolean} */ isPre) => (preorder ? preorder.line(item, isPre) : item);
    // Charm first: the cart lists the newest line on top, so the band leads the look in the cart
    const items = [
      line({ id: Number(this.dataset.charmId), quantity: 1 }, this.dataset.charmPre === 'true'),
      line({ id: Number(this.dataset.bandId), quantity: 1 }, this.dataset.bandPre === 'true'),
    ];
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      // @ts-ignore Theme is a global set by the theme's scripts snippet
      const routes = Theme.routes;
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cl-complete-look', sourceId: 'cl-complete-look', itemCount: 2, sections: response.sections, didError },
      });
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error instanceof HTMLElement) {
          error.textContent = response.description || response.message || 'Could not add to cart.';
          error.hidden = false;
        }
      } else {
        const label = this.querySelector('[ref="label"]');
        if (label) label.textContent = 'Added ✓';
        clTrack('set_added', {
          source: 'complete_look',
          band: this.dataset.band,
          charm: this.dataset.charm,
          value: Number(this.dataset.value || 0) / 100,
          currency: this.dataset.currency || 'EUR',
        });
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (error instanceof HTMLElement) {
        error.textContent = 'Could not add to cart. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cl-complete-look')) customElements.define('cl-complete-look', ClaspLabCompleteLook);
